import axios from 'axios'
import ConfigAPI from '@/config/api'
import Session from '@/handler/session'
import Helper from '@/handler/helper'

const INTERVAL_MS = 5 * 60 * 1000
const MAX_OFFLINE_QUEUE_SIZE = 200
const MAX_OFFLINE_QUEUE_AGE_MS = 24 * 60 * 60 * 1000
const MAX_ACCEPTABLE_ACCURACY_M = 500
const MAX_MOVEMENT_SPEED_KMH = 130
const MAX_MOVEMENT_BASE_KM = 3
const GEOLOCATION_TIMEOUT_MS = 15000
const STORAGE_PREFIX = window.location.origin + '_SellerLocation'
const KEY_LAST_SENT = STORAGE_PREFIX + '_LastSentAt'
const KEY_PERMISSION = STORAGE_PREFIX + '_Permission'
const KEY_LAST_ACCEPTED_POSITION = STORAGE_PREFIX + '_LastAcceptedPosition'
const KEY_OFFLINE_QUEUE = STORAGE_PREFIX + '_OfflineQueue'

let timer = null
let started = false
let lastAttemptAt = 0
let syncing = false

function getLastSent() {
  const value = localStorage.getItem(KEY_LAST_SENT)
  return value ? parseInt(value, 10) : 0
}

function setLastSent(ts) {
  localStorage.setItem(KEY_LAST_SENT, ts.toString())
  window.dispatchEvent(new CustomEvent('seller-location-last-sent', { detail: { timestamp: ts } }))
}

function setPermissionState(state) {
  localStorage.setItem(KEY_PERMISSION, state)
  window.dispatchEvent(new CustomEvent('seller-location-permission', { detail: { state } }))
}

function getLastAcceptedPosition() {
  try {
    const raw = localStorage.getItem(KEY_LAST_ACCEPTED_POSITION)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    if (!parsed) {
      return null
    }
    const lat = typeof parsed.lat === 'number' ? parsed.lat : null
    const lng = typeof parsed.lng === 'number' ? parsed.lng : null
    const timestamp = typeof parsed.timestamp === 'number' ? parsed.timestamp : null
    if (lat === null || lng === null || timestamp === null) {
      return null
    }
    return {
      lat,
      lng,
      timestamp,
      accuracy: typeof parsed.accuracy === 'number' ? parsed.accuracy : null,
    }
  } catch (e) {
    return null
  }
}

function setLastAcceptedPosition({ lat, lng, accuracy, timestamp }) {
  localStorage.setItem(KEY_LAST_ACCEPTED_POSITION, JSON.stringify({
    lat,
    lng,
    accuracy,
    timestamp,
  }))
}

function getOfflineQueue() {
  try {
    const raw = localStorage.getItem(KEY_OFFLINE_QUEUE)
    return raw ? JSON.parse(raw) : []
  } catch (e) {
    return []
  }
}

function addToOfflineQueue(payload) {
  let queue = getOfflineQueue()
  queue.push(payload)
  // Mantener solo las últimas MAX_OFFLINE_QUEUE_SIZE entradas
  if (queue.length > MAX_OFFLINE_QUEUE_SIZE) {
    queue = queue.slice(-MAX_OFFLINE_QUEUE_SIZE)
  }
  localStorage.setItem(KEY_OFFLINE_QUEUE, JSON.stringify(queue))
}

async function flushOfflineQueue() {
  if (syncing) return
  const queue = getOfflineQueue()
  if (!queue.length) return

  const url = buildUrl()
  if (!url) return

  syncing = true
  const now = Date.now()
  const remaining = []
  for (const item of queue) {
    // Descartar ubicaciones con más de 24 horas (ya no tienen valor)
    if (item.captured_at && (now - new Date(item.captured_at).getTime()) > MAX_OFFLINE_QUEUE_AGE_MS) {
      continue
    }
    try {
      const instance = axios.create()
      await instance.post(url, item)
    } catch (e) {
      remaining.push(item)
    }
  }
  localStorage.setItem(KEY_OFFLINE_QUEUE, JSON.stringify(remaining))
  if (remaining.length === 0) {
    setLastSent(Date.now())
  }
  syncing = false
}

function calcDistanceKm(lat1, lng1, lat2, lng2) {
  const toRad = (value) => value * Math.PI / 180
  const R = 6371
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

function isLogicalPosition(currentPosition, nowTs) {
  if (currentPosition.accuracy === null || currentPosition.accuracy > MAX_ACCEPTABLE_ACCURACY_M) {
    return false
  }

  const lastPosition = getLastAcceptedPosition()
  if (!lastPosition) {
    return true
  }

  const elapsedMs = Math.max(0, nowTs - lastPosition.timestamp)
  const elapsedHours = elapsedMs / 3600000
  const maxDistanceKm = MAX_MOVEMENT_BASE_KM + (elapsedHours * MAX_MOVEMENT_SPEED_KMH)
  const distanceKm = calcDistanceKm(
    lastPosition.lat,
    lastPosition.lng,
    currentPosition.lat,
    currentPosition.lng
  )

  if (distanceKm <= maxDistanceKm) {
    return true
  }

  return false
}

function getPermissionState() {
  return localStorage.getItem(KEY_PERMISSION) || 'unknown'
}

async function checkPermission() {
  if (!navigator.permissions || !navigator.permissions.query) {
    return getPermissionState()
  }

  try {
    const status = await navigator.permissions.query({ name: 'geolocation' })
    setPermissionState(status.state)

    status.onchange = () => {
      setPermissionState(status.state)
      if (status.state === 'granted') {
        localStorage.removeItem(KEY_LAST_ACCEPTED_POSITION)
        scheduleNext()
      }
    }

    return status.state
  } catch (e) {
    return getPermissionState()
  }
}

function hasSeller() {
  const seller = Helper.getSeller()
  return seller && seller.id
}

function scheduleNext() {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }

  const lastSent = getLastSent()
  const last = Math.max(lastSent, lastAttemptAt)
  const now = Date.now()
  let wait = INTERVAL_MS

  if (!last || now - last >= INTERVAL_MS) {
    wait = 0
  } else {
    wait = INTERVAL_MS - (now - last)
  }

  timer = setTimeout(async () => {
    lastAttemptAt = Date.now()
    await send({ source: 'interval' })
    scheduleNext()
  }, wait)
}

function buildUrl() {
  if (!Session.getToken()) {
    return ''
  }
  return ConfigAPI.baseURL + 'sellers-location' + Session.getToken()
}

function clearStuckState() {
  localStorage.removeItem(KEY_LAST_ACCEPTED_POSITION)
}

function send({ order_id = null, source = 'interval', observation = null } = {}) {
  if (!Session.getToken()) {
    return Promise.resolve(false)
  }
  if (!hasSeller()) {
    return Promise.resolve(false)
  }

  return new Promise(async (resolve) => {
    if (!navigator.geolocation) {
      setPermissionState('unsupported')
      return resolve(false)
    }

    const permState = await checkPermission()
    if (permState === 'granted') {
      const lastSent = getLastSent()
      const now = Date.now()
      if (lastSent === 0 || (now - lastSent) > 2 * INTERVAL_MS) {
        clearStuckState()
      }
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPermissionState('granted')

        const accuracyValue = (pos && pos.coords && typeof pos.coords.accuracy === 'number')
          ? pos.coords.accuracy
          : null
        if (accuracyValue === null) {
          return resolve(false)
        }

        const latitude = (pos && pos.coords && typeof pos.coords.latitude === 'number')
          ? pos.coords.latitude
          : null
        const longitude = (pos && pos.coords && typeof pos.coords.longitude === 'number')
          ? pos.coords.longitude
          : null
        if (latitude === null || longitude === null) {
          return resolve(false)
        }

        const nowTs = Date.now()
        const currentPosition = {
          lat: latitude,
          lng: longitude,
          accuracy: accuracyValue,
        }
        if (!isLogicalPosition(currentPosition, nowTs)) {
          localStorage.removeItem(KEY_LAST_ACCEPTED_POSITION)
          return resolve(false)
        }

        const payload = {
          lat: latitude.toString(),
          lng: longitude.toString(),
          accuracy: accuracyValue.toString(),
          source: source,
          observation: observation,
          order_id: order_id,
        }

        setLastAcceptedPosition({
          lat: latitude,
          lng: longitude,
          accuracy: accuracyValue,
          timestamp: nowTs,
        })

        // Sin conexión: guardar en cola para sincronizar después
        if (!navigator.onLine) {
          const capturedAtStr = new Date(nowTs).toISOString().slice(0, 19).replace('T', ' ')
          addToOfflineQueue({ ...payload, captured_at: capturedAtStr })
          resolve(true)
          return
        }

        const url = buildUrl()
        const instance = axios.create()

        instance.post(url, payload)
          .then(() => {
            setLastSent(nowTs)
            resolve(true)
          })
          .catch(() => {
            // Si falló por red, encolar para reintento
            addToOfflineQueue(payload)
            resolve(false)
          })
      },
      (err) => {
        if (err && err.code === 1) {
          setPermissionState('denied')
        }
        resolve(false)
      },
      { enableHighAccuracy: true, timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: 0 }
    )
  })
}

function start() {
  if (started) {
    return
  }
  started = true

  if (!hasSeller()) {
    return
  }

  checkPermission()
  scheduleNext()

  // Al recuperar conexión, sincronizar ubicaciones pendientes
  window.addEventListener('online', flushOfflineQueue)

  // Al volver a primer plano (pantalla encendida / app activa), re-evaluar el timer.
  // En mobile los setTimeout se pausan cuando la pantalla está apagada.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      scheduleNext()
    }
  })
}

export default {
  start,
  send,
  checkPermission,
  getPermissionState,
  getLastSent,
  flushOfflineQueue,
}
